// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Utility functions for palette category organization
 */

import type { CategoryBucket, PaletteItem } from '../types'

/**
 * Builds category buckets from a list of palette items.
 * Items are grouped by their display.category and sorted by order then label.
 */
export const buildCategories = <T extends PaletteItem>(items: T[]): CategoryBucket<T>[] => {
  const buckets = new Map<string, CategoryBucket<T>>()

  const ensureBucket = (item: T): CategoryBucket<T> => {
    const type = item.display.category
    const existing = buckets.get(type)
    if (existing) {
      return existing
    }

    const bucket: CategoryBucket<T> = {
      type,
      label: item.display.categoryLabel,
      order: item.display.categoryOrder,
      icon: item.display.icon,
      items: [],
    }

    buckets.set(type, bucket)
    return bucket
  }

  for (const item of items) {
    const bucket = ensureBucket(item)
    bucket.items.push(item)
  }

  // Sort items within each bucket
  for (const bucket of buckets.values()) {
    bucket.items.sort((a, b) => {
      if (a.display.order !== b.display.order) {
        return a.display.order - b.display.order
      }
      return a.display.label.localeCompare(b.display.label)
    })
  }

  // Sort and filter buckets
  return Array.from(buckets.values())
    .filter((bucket) => bucket.items.length > 0)
    .sort((a, b) => {
      if (a.order !== b.order) {
        return a.order - b.order
      }
      return a.label.localeCompare(b.label)
    })
}

/**
 * Filters items by search term, matching against label and description.
 */
export const filterCategoriesBySearch = <T extends PaletteItem>(
  categories: CategoryBucket<T>[],
  searchTerm: string
): CategoryBucket<T>[] => {
  if (!searchTerm) {
    return categories
  }

  const searchLower = searchTerm.toLowerCase()

  return categories
    .map((category) => ({
      ...category,
      items: category.items.filter((item) => {
        return (
          item.display.label.toLowerCase().includes(searchLower) ||
          item.display.description.toLowerCase().includes(searchLower)
        )
      }),
    }))
    .filter((category) => category.items.length > 0)
}
